import { ISelectedMember } from '@/types';
import {
  IStuDeductibleOption,
  IStuEffectiveParams,
  IStuJourney,
  IStuPlan,
  IStuQuoteRequest,
  IStuQuoteResponse,
  IStuQuoteSelected,
  IStuUnavailablePlan,
} from '@/types/stu';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createStuQuote,
  getStuJourneyList,
  getStuQuote,
  QuoteApiError,
} from '@/queries/super-top-up.queries';
import { getMemberAndDependentsDetails } from '@/queries/memberDetails.queries';
import {
  STU_QUERY_KEYS,
  STU_SESSION_KEYS,
  STU_TIMEOUT_VALUES,
} from '@/constants/stuQueryKeys';
import { useLocation, useNavigate } from 'react-router-dom';
import useSessionStorage from '@/hooks/useSessionStorage';
import { useShowStuError } from '@/hooks/useShowStuError';
import {
  isTransientError,
  retryOnTransientError,
} from '@/utils/axios/baseAxiosInstance';
import {
  isStuQuoteLocked,
  resolveStuError,
  stuErrorCode,
  stuErrorMessage,
  stuPlanRefusal,
  toastStuError,
} from '@/utils/stuError';
import { STU_BASE_PATH, getJourneyPath, markResumeReturn, returnsToPlanList, stuPaths } from '@/utils/stu/journey';
import { useAppContext } from '@/context/AppContext';
import { STU_ERROR_CODES, STU_PHONE_NUMBER, STU_WHATSAPP_INITIAL_MESSAGE } from '@/constants/superTopupConfig';
import { STU_ERROR_COPY } from '@/constants/stuErrorCopy';
import { whatsAppMessageUtil } from '@/utils/whatsAppMessageUtil';

const DEFAULT_SELECTED_MEMBERS: ISelectedMember[] = [];

// Define the shape of your context, now including relationType
interface ICreateQuoteOptions {
  /** The new quote is ready to show on the plan list. */
  onFreshQuote?: () => void;
  /** The server refused the members (`STU_INVALID_QUOTE_REQUEST`); gets its reason instead of the error page. */
  onInvalidRequest?: (message: string) => void;
}

interface StuJourneyContext {
  leadId: string;
  setLeadId: React.Dispatch<string>;
  quoteId: string;
  setQuoteId: React.Dispatch<string>;
  quotePlans: IStuPlan[];
  quoteUnavailablePlans: IStuUnavailablePlan[];
  /** The grid the super top-up picker is built from; empty on health or before the first poll. */
  quoteDeductibleOptions: IStuDeductibleOption[];
  quoteTenureOptions: number[];
  /** What the current quote was asked for, as the server echoes it. */
  quoteSelected: IStuQuoteSelected | null;
  quoteRequest: IStuQuoteRequest | null;
  effectiveParams: IStuEffectiveParams | null;
  createQuote: (
    request: IStuQuoteRequest,
    options?: ICreateQuoteOptions,
  ) => void;
  isCreatingQuote: boolean;
  benefitId: string;
  setBenefitId: React.Dispatch<string>;
  selectedMembers: ISelectedMember[];
  proposalId: string;
  setSelectedMembers: React.Dispatch<ISelectedMember[]>;
  isLoadingQuotes: boolean;
  isFetchingQuotes: boolean;
  quotesError: unknown;
  refetchQuotes: () => void;
  resetQuoteCache: () => void;
  routeToJourney: (
    journey: IStuJourney,
    options?: { replace?: boolean; fromResume?: boolean },
  ) => void;
  handleQuoteLocked: (lockedQuoteId: string) => Promise<void>;
  /**
   * Routes a plan id that can't be used (or saved add-ons that can't be priced) from any
   * plan-scoped call; `retry` repeats that call while the plan list is being re-quoted.
   * True when the error was one of these and has been handled.
   */
  handlePlanCode: (error: unknown, retry?: () => void) => boolean;
  /** The plan id a re-quote carried the journey to: its new draft has no documents yet. */
  carriedPlanId: string;
  setCarriedPlanId: React.Dispatch<string>;
}

// Create the context with default values
const StuJourneyContext = createContext<
  StuJourneyContext | undefined
>(undefined);

/** Guide 2026-10-14 §2: PLAN_LIST_UPDATING is repeated after retryAfterMs, at most 6 times. */
const PLAN_LIST_UPDATING_MAX_TRIES = 6;
const PLAN_LIST_UPDATING_RETRY_MS = 5000;

const EMPTY_PLANS: IStuPlan[] = [];
const EMPTY_UNAVAILABLE_PLANS: IStuUnavailablePlan[] = [];
const EMPTY_DEDUCTIBLE_OPTIONS: IStuDeductibleOption[] = [];
const EMPTY_TENURE_OPTIONS: number[] = [];
/**
 * Guide 2026-10-20 (a KYC correction keeps the plan id) §3.2: after a correction, reading the quote
 * re-quotes the lead and gives every plan a new id, the one being bought included. So it's read
 * only where plans are on screen: the plan list, select members (which waits for the plans before
 * opening the list), and the v1 screens built on them. Not on home, the agent landing, error or
 * thank-you, nor anywhere from the add-on page onwards.
 */
const STU_PLAN_LIST_SCREEN =
  /^\/retail\/(?:health-insurance|super-top-up)\/(plans|select-member|recommendation|plans-benefits|add-riders\/[^/]+|verify-details|add-nominee|member-details(\/[^/]+)?|checkout|bank-details)\/?$/;

// Create a provider component
export const StuJourneyProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname, key: locationKey } = useLocation();
  const { isWebView } = useAppContext();
  const isPlanListScreen = STU_PLAN_LIST_SCREEN.test(pathname);
  // Changes on every arrival at the plan list, including coming back to it from another page.
  const planListVisitKey =
    pathname.replace(/\/$/, '') === stuPaths.plans ? locationKey : '';

  const [benefitId, setBenefitId] = useSessionStorage<string>(STU_SESSION_KEYS.BENEFIT_ID, '');
  const [leadId, setLeadId] = useSessionStorage<string>(STU_SESSION_KEYS.LEAD_ID, '');
  const [quoteId, setQuoteId] = useSessionStorage<string>(
    STU_SESSION_KEYS.QUOTE_ID,
    '',
  );
  const [quoteRequest, setQuoteRequest] =
    useSessionStorage<IStuQuoteRequest | null>(
      STU_SESSION_KEYS.QUOTE_REQUEST,
      null,
    );
  const [effectiveParams, setEffectiveParams] =
    useSessionStorage<IStuEffectiveParams | null>(
      STU_SESSION_KEYS.EFFECTIVE_PARAMS,
      null,
    );
  const [selectedPlanId, setSelectedPlanId] = useSessionStorage<string>(STU_SESSION_KEYS.SELECTED_PLAN_ID, '');
  const [selectedMembers, setSelectedMembers] = useSessionStorage<ISelectedMember[]>(STU_SESSION_KEYS.SELECTED_MEMBERS, DEFAULT_SELECTED_MEMBERS);

  const [proposalIdState, setProposalIdState] = useSessionStorage<string>(STU_SESSION_KEYS.PROPOSAL_ID, '');
  const [carriedPlanId, setCarriedPlanId] = useSessionStorage<string>(STU_SESSION_KEYS.CARRIED_PLAN_ID, '');

  const queryClient = useQueryClient();

  const hasSelectedPlan = !!selectedPlanId;
  const [hasQuotePollTimedOut, setHasQuotePollTimedOut] = useState(false);

  useEffect(() => {
    setHasQuotePollTimedOut(false);
    if (!quoteId) return;

    const pollTimeout = setTimeout(
      () => setHasQuotePollTimedOut(true),
      STU_TIMEOUT_VALUES.QUOTE_POLL_TIMEOUT_MS,
    );

    return () => clearTimeout(pollTimeout);
  }, [quoteId, planListVisitKey]);

  const quoteQueryOptions = getStuQuote(quoteId ?? '');

  const {
    data: quoteResponse,
    isLoading: isQuoteResponseLoading,
    isFetching: isFetchingQuotes,
    error: quotesError,
    refetch: refetchQuotes,
  } = useQuery<IStuQuoteResponse>({
    ...quoteQueryOptions,
    enabled: quoteQueryOptions.enabled && isPlanListScreen,
    refetchInterval: hasQuotePollTimedOut
      ? false
      : quoteQueryOptions.refetchInterval,
    staleTime: hasSelectedPlan ? Infinity : 5 * 60 * 1000,
    refetchOnMount: hasSelectedPlan ? false : true,
    refetchOnReconnect: hasSelectedPlan ? false : true,
  });

  const isLoadingQuotes =
    isQuoteResponseLoading ||
    (quoteResponse?.status === 'IN_PROGRESS' && !hasQuotePollTimedOut);

  const quotePlans = quoteResponse?.plans ?? EMPTY_PLANS;
  const quoteUnavailablePlans =
    quoteResponse?.notAvailable ?? EMPTY_UNAVAILABLE_PLANS;
  const quoteDeductibleOptions =
    quoteResponse?.deductibleOptions ?? EMPTY_DEDUCTIBLE_OPTIONS;
  const quoteTenureOptions = quoteResponse?.tenureOptions ?? EMPTY_TENURE_OPTIONS;
  const quoteSelected = quoteResponse?.selected ?? null;

  const latestQuoteRequestRef = useRef<IStuQuoteRequest | null>(null);
  const shownQuoteErrorRef = useRef<unknown>();
  const quoteIdRef = useRef(quoteId);
  const quoteRequestRef = useRef(quoteRequest);
  quoteIdRef.current = quoteId;
  quoteRequestRef.current = quoteRequest;
  const previousQuoteRef = useRef<{
    quoteId: string;
    request: IStuQuoteRequest | null;
  }>({ quoteId: '', request: null });
  const invalidRequestRef = useRef<{
    request: IStuQuoteRequest;
    onInvalidRequest: (message: string) => void;
  } | null>(null);

  const showStuError = useShowStuError();

  const showQuoteError = useCallback(
    (error: unknown) => showStuError(error, resolveStuError(error)),
    [showStuError],
  );

  const createQuoteMutation = useMutation({
    ...createStuQuote(),
    retry: retryOnTransientError,
    retryDelay: 1000,
    // Every POST /quotes prices a new quote: always the plan list for its plumQuoteId.
    onSuccess: ({ plumQuoteId, effectiveParams: params }, request) => {
      if (
        latestQuoteRequestRef.current &&
        request !== latestQuoteRequestRef.current
      )
        return;
      setQuoteId(plumQuoteId);
      setEffectiveParams(params ?? null);
    },
    onError: (error, request) => {
      if (
        latestQuoteRequestRef.current &&
        request !== latestQuoteRequestRef.current
      )
        return;

      const { quoteId: previousQuoteId, request: previousRequest } =
        previousQuoteRef.current;
      const invalid = invalidRequestRef.current;
      const invalidReason =
        invalid?.request === request &&
        stuErrorCode(error) === STU_ERROR_CODES.INVALID_QUOTE_REQUEST
          ? stuErrorMessage(error)
          : undefined;

      if (previousQuoteId) {
        latestQuoteRequestRef.current = previousRequest;
        setQuoteId(previousQuoteId);
        setQuoteRequest(previousRequest);
      }

      if (invalid && invalidReason) {
        invalid.onInvalidRequest(invalidReason);
        return;
      }

      if (!previousQuoteId) {
        showQuoteError(error);
        return;
      }

      toastStuError(
        error,
        'Unable to update plans. Please try again.',
        'stu-quote-update-error',
      );
    },
  });

  const { mutate: createQuoteMutate } = createQuoteMutation;

  // Back on the plan list from any page: read the quote again and poll it as on first arrival,
  // rather than show what was cached when they left. A quote being created fetches on its own.
  const isCreatingQuoteRef = useRef(false);
  isCreatingQuoteRef.current = createQuoteMutation.isPending;
  useEffect(() => {
    if (!planListVisitKey || !quoteIdRef.current || isCreatingQuoteRef.current) return;
    refetchQuotes();
  }, [planListVisitKey, refetchQuotes]);

  const createQuote = useCallback(
    (
      request: IStuQuoteRequest,
      { onFreshQuote, onInvalidRequest }: ICreateQuoteOptions = {},
    ) => {
      invalidRequestRef.current = onInvalidRequest
        ? { request, onInvalidRequest }
        : null;
      if (quoteIdRef.current) {
        previousQuoteRef.current = {
          quoteId: quoteIdRef.current,
          request: quoteRequestRef.current ?? null,
        };
      }
      latestQuoteRequestRef.current = request;
      setQuoteId('');
      setQuoteRequest(request);
      setEffectiveParams(null);
      createQuoteMutate(request, { onSuccess: () => onFreshQuote?.() });
    },
    [createQuoteMutate, setQuoteId, setQuoteRequest, setEffectiveParams],
  );


  useEffect(() => {
    if (!benefitId) return;

    queryClient.prefetchQuery(getMemberAndDependentsDetails(benefitId));
  }, [benefitId, queryClient]);

  const resetQuoteCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: [STU_QUERY_KEYS.GET_QUOTE] });
  }, [queryClient]);

  const resumeJourney = useCallback(
    (journey: IStuJourney) => {
      resetQuoteCache();
      setQuoteRequest(null);
      setEffectiveParams(null);
      setQuoteId(journey.quoteId);
      setSelectedPlanId(journey.planId ? String(journey.planId) : '');
      setProposalIdState(journey.proposalId ?? '');
    },
    [
      resetQuoteCache,
      setQuoteRequest,
      setEffectiveParams,
      setQuoteId,
      setSelectedPlanId,
      setProposalIdState,
    ],
  );

  const routeToJourney = useCallback(
    (
      journey: IStuJourney,
      { replace = false, fromResume = false } = {},
    ) => {
      const path = getJourneyPath(journey);
      if (!path) {
        whatsAppMessageUtil(
          STU_PHONE_NUMBER,
          STU_WHATSAPP_INITIAL_MESSAGE,
          isWebView,
        );
        return;
      }

      // Backing out of a resumed, unpaid stage lands on its plan list: mark the
      // page being left, so returning to it redirects there.
      const paths = stuPaths;
      if (fromResume && returnsToPlanList(journey)) {
        markResumeReturn(locationKey, paths.plans);
      }

      const isNewQuote = path === paths.newQuote;
      if (!isNewQuote) resumeJourney(journey);
      window.scrollTo({ top: 0, behavior: 'instant' });
      navigate(path, { replace });
    },
    [isWebView, locationKey, navigate, resumeJourney],
  );

  const planListUpdatingTriesRef = useRef(0);
  const planListUpdatingRetryRef = useRef<ReturnType<typeof setTimeout>>();

  // A waiting repeat belongs to the page and quote it was made on: once the customer leaves,
  // it must not commit the earlier call.
  useEffect(
    () => () => clearTimeout(planListUpdatingRetryRef.current),
    [pathname, quoteId],
  );

  // Guide 2026-10-14 §2: route on the code, never the message.
  const handlePlanCode = useCallback(
    (error: unknown, retry?: () => void) => {
      const refusal = stuPlanRefusal(error);
      if (!refusal) return false;
      if (refusal.kind !== 'updating') planListUpdatingTriesRef.current = 0;

      switch (refusal.kind) {
        case 'replaced':
          // Same plan, new id: drop the old proposal and choose again on the new id's plan page.
          if (!refusal.planId) break;
          setSelectedPlanId(String(refusal.planId));
          setProposalIdState('');
          setCarriedPlanId(String(refusal.planId));
          navigate(stuPaths.plans, { replace: true });
          return true;
        case 'updating':
          // The list is being re-quoted: repeat the same call, at most 6 times (~30 s).
          planListUpdatingTriesRef.current += 1;
          if (
            retry &&
            planListUpdatingTriesRef.current <= PLAN_LIST_UPDATING_MAX_TRIES
          ) {
            clearTimeout(planListUpdatingRetryRef.current);
            planListUpdatingRetryRef.current = setTimeout(
              retry,
              refusal.retryAfterMs ?? PLAN_LIST_UPDATING_RETRY_MS,
            );
            return true;
          }
          planListUpdatingTriesRef.current = 0;
          break;
        case 'gone':
          toast.error(STU_ERROR_COPY[refusal.code].title, {
            id: 'stu-plan-unavailable',
          });
          break;
        case 'addons':
          // The saved add-ons can't be priced as they stand: adjust them on the plan page.
          if (selectedPlanId) {
            navigate(stuPaths.plans, {
              replace: true,
            });
            return true;
          }
          break;
        default:
          break;
      }
      navigate(stuPaths.plans, { replace: true });
      return true;
    },
    [navigate, selectedPlanId, setSelectedPlanId, setProposalIdState, setCarriedPlanId],
  );

  const handleQuoteLocked = useCallback(
    async (lockedQuoteId: string) => {
      const journey = await queryClient
        .fetchQuery({ ...getStuJourneyList(), staleTime: 0 })
        .then(({ journeys }) =>
          journeys.find((item) => item.quoteId === lockedQuoteId),
        )
        .catch(() => undefined);

      if (journey) {
        routeToJourney(journey, { replace: true });
        return;
      }
      // Not in the list (or the list didn't load): say so here, and Try again looks it up again.
      showStuError(undefined, STU_ERROR_COPY[STU_ERROR_CODES.QUOTE_LOCKED]);
    },
    [queryClient, routeToJourney, showStuError],
  );

  useEffect(() => {
    const isInsurerApiError =
      quotesError instanceof QuoteApiError ||
      (typeof quotesError === 'object' &&
        quotesError !== null &&
        (quotesError as { code?: string }).code === 'INSURER_API_ERROR');

    const isTransient = isTransientError(quotesError);

    if (!quotesError || !quoteId || isInsurerApiError || isTransient) return;
    // Home doesn't need the quote; a failure there mustn't send the customer away from it.
    if (pathname === stuPaths.home || pathname === STU_BASE_PATH) return;
    // Each failure is shown once: leaving its error screen (e.g. Go to home) doesn't bring
    // it back. A new failure, after Try again, is a new error and shows.
    if (shownQuoteErrorRef.current === quotesError) return;

    shownQuoteErrorRef.current = quotesError;
    // Paid, or expired but still payable: its journey card says where to go.
    if (isStuQuoteLocked(quotesError)) {
      handleQuoteLocked(quoteId);
      return;
    }
    showQuoteError(quotesError);
  }, [quotesError, quoteId, showQuoteError, pathname, handleQuoteLocked]);


  return (
    <StuJourneyContext.Provider
      value={{
        selectedMembers: selectedMembers ?? [],
        setSelectedMembers,
        benefitId: benefitId ?? '',
        setBenefitId,
        leadId: leadId ?? '',
        setLeadId,
        quoteId: quoteId ?? '',
        setQuoteId,
        quotePlans,
        quoteUnavailablePlans,
        quoteDeductibleOptions,
        quoteTenureOptions,
        quoteSelected,
        quoteRequest: quoteRequest ?? null,
        effectiveParams: effectiveParams ?? null,
        createQuote,
        isCreatingQuote: createQuoteMutation.isPending,
        proposalId: proposalIdState ?? '',
        isLoadingQuotes,
        isFetchingQuotes,
        quotesError,
        refetchQuotes,
        resetQuoteCache,
        routeToJourney,
        handleQuoteLocked,
        handlePlanCode,
        carriedPlanId: carriedPlanId ?? '',
        setCarriedPlanId,
      }}
    >
      {children}
    </StuJourneyContext.Provider>
  );
};

// Create a custom hook for easier access
export const useStuJourney = () => {
  const context = useContext(StuJourneyContext);
  if (!context) {
    throw new Error(
      'useRelationContext must be used within a RelationProvider',
    );
  }
  return context;
};
